/**
 * Read adapters for stored categorical share payloads (plots plan L; contract
 * in `../contract.ts`, shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: `value` becomes `share` as stored (never re-normalised, which is
 * what fixes stored lane decks too), `topCategoryId` becomes
 * `topCategoryIds`, `page.index` becomes `page.current`, and a category's slot
 * is its position (today's slot rule). The folded tail is recognised by its
 * machine id (`other_responses`, `__other__`), never by its label.
 *
 * One routing read, flagged to the owner (L-design Q1): a stored lane
 * exhibit carries no typed multi-select flag, so a stored row whose shares
 * add up past 100 is multi-response data and is drawn as grouped bars; the
 * old slide squeezed it into one re-normalised 100% bar.
 */

import { PlotReadAdapter, StoredPlotPayload } from '../contract'

import { CategoricalCategory, CategoricalRow } from '@digital-twins/slide-schemas/generated/plot-types'

/** The `matrix-categorical-bars` slide as stored (`plot` optional). */
export interface StoredMatrixCategoricalSlide {
  type: 'matrix-categorical-bars'
  categories: Array<{ id: string; label: string }>
  rows: Array<{
    id: string
    label: string
    baseSize?: number | null
    topCategoryId?: string | null
    imageUrl?: string | null
    bars: Array<{ categoryId: string; label?: string; value: number; count?: number | null; isTop?: boolean }>
  }>
  page?: { index: number; total: number } | null
  plot?: { kind: string; data: unknown } | null
}

/** A deck segment tab's `matrix-categorical` inner view (`ChartSegmentMatrixCategoricalView`). */
export interface StoredMatrixCategoricalView {
  kind: 'matrix-categorical'
  categories: Array<{ id: string; label: string }>
  rows: Array<{ id: string; label: string; bars: Array<{ categoryId: string; label?: string; value: number | null }> }>
}

export type StoredCategoricalPayload = StoredPlotPayload<'stacked-100-bars'> | StoredPlotPayload<'grouped-bars'>

const OTHER_IDS = new Set(['other_responses', '__other__'])
/**
 * Legacy-only read (plan decision 22, the documented exception): a stored lane
 * slide carries no multi-select flag, so a row whose shares add up past this
 * is read as multi-response data. A single-select row stores unrounded shares
 * that add up to 100 and cannot pass 100.5. Stamped plots never reach this:
 * they carry the typed `multiResponse`.
 */
const SINGLE_ANSWER_SUM_MAX = 100.5
/** Legacy display rule: whole numbers bare, else one decimal. */
const LEGACY_DECIMALS = 1

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function categories(stored: Array<{ id: string; label: string }>): Array<CategoricalCategory> {
  return stored.map((category, index) => {
    const isOther = OTHER_IDS.has(category.id)
    return {
      id: category.id,
      label: category.label,
      role: isOther ? 'other' : 'option',
      colorSlot: isOther ? null : (index % 8) + 1,
    }
  })
}

/** Legacy stored rows only (see SINGLE_ANSWER_SUM_MAX); stamped plots use `multiResponse`. */
function isMultiResponse(rows: Array<{ bars: Array<{ value: number | null }> }>): boolean {
  return rows.some((row) => row.bars.reduce((sum, bar) => sum + (typeof bar.value === 'number' ? bar.value : 0), 0) > SINGLE_ANSWER_SUM_MAX)
}

function payload(
  cats: Array<CategoricalCategory>,
  rows: Array<CategoricalRow>,
  multiResponse: boolean,
  page?: { index: number; total: number } | null,
): StoredCategoricalPayload {
  const data = {
    valueUnit: 'percent' as const,
    basis: 'respondents' as const,
    multiResponse,
    categories: cats,
    rows: multiResponse ? rows.map((row, index) => ({ ...row, colorSlot: (index % 8) + 1 })) : rows,
    decimals: LEGACY_DECIMALS,
    ...(page && page.total > 1 ? { page: { current: page.index, total: page.total } } : {}),
    labels: {},
  }
  return multiResponse ? { kind: 'grouped-bars', data } : { kind: 'stacked-100-bars', data }
}

/** A stored slide without `plot`. */
export const legacyMatrixCategoricalSlideAdapter: PlotReadAdapter<'stacked-100-bars', StoredMatrixCategoricalSlide> = {
  kind: 'stacked-100-bars',
  source: { surface: 'deck-slide', shape: 'matrix-categorical-bars' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredMatrixCategoricalSlide {
    return isRecord(stored) && stored.type === 'matrix-categorical-bars' && Array.isArray(stored.rows) && Array.isArray(stored.categories)
  },
  read(stored: StoredMatrixCategoricalSlide): StoredPlotPayload<'stacked-100-bars'> {
    return readStoredMatrixCategoricalSlide(stored) as StoredPlotPayload<'stacked-100-bars'>
  },
}

/** Stored slide → plot payload (stacked, or grouped for stored multi-response rows). */
export function readStoredMatrixCategoricalSlide(stored: StoredMatrixCategoricalSlide): StoredCategoricalPayload {
  const cats = categories(stored.categories || [])
  const known = new Set(cats.map((category) => category.id))
  const rows: Array<CategoricalRow> = (stored.rows || []).map((row) => ({
    id: row.id,
    label: row.label,
    kind: 'statement',
    base: typeof row.baseSize === 'number' ? row.baseSize : null,
    lowBase: false,
    imageUrl: row.imageUrl ?? null,
    bars: (row.bars || [])
      .filter((bar) => known.has(bar.categoryId))
      .map((bar) => ({ categoryId: bar.categoryId, share: Math.max(0, Number(bar.value) || 0), count: bar.count ?? null })),
    topCategoryIds: row.topCategoryId ? [row.topCategoryId] : [],
  }))
  return payload(cats, rows, isMultiResponse(stored.rows || []), stored.page)
}

/** A stored segment tab's inner view: a null cell had no answered base and is not drawn. */
export function readStoredMatrixCategoricalView(stored: StoredMatrixCategoricalView): StoredCategoricalPayload {
  const cats = categories(stored.categories || [])
  const rows: Array<CategoricalRow> = (stored.rows || []).map((row) => ({
    id: row.id,
    label: row.label,
    kind: 'statement',
    base: null,
    lowBase: false,
    bars: row.bars
      .filter((bar) => typeof bar.value === 'number' && Number.isFinite(bar.value))
      .map((bar) => ({ categoryId: bar.categoryId, share: Math.max(0, bar.value as number) })),
    topCategoryIds: [],
  }))
  return payload(cats, rows, isMultiResponse(stored.rows || []))
}

/**
 * The plot a stored `matrix-categorical-bars` slide draws: its stamped `plot`
 * when it is a categorical kind, else the legacy reader.
 */
export function readMatrixCategoricalSlide(stored: StoredMatrixCategoricalSlide): { payload: StoredCategoricalPayload; isStamped: boolean } {
  const plot = stored.plot
  if (isRecord(plot) && (plot.kind === 'stacked-100-bars' || plot.kind === 'grouped-bars') && isRecord(plot.data)) {
    return { payload: plot as unknown as StoredCategoricalPayload, isStamped: true }
  }
  return { payload: readStoredMatrixCategoricalSlide(stored), isStamped: false }
}

/** A quick-study result as stored, `stacked_100_bars_plot` optional (W6-QS-b). */
export interface StoredQuickStudyCategoricalGrid {
  stacked_100_bars_plot?: unknown
}

/**
 * A quick-study categorical grid as Python stamped it
 * (`quick_study.categorical_grid_plot`): read as is. A result stored before the
 * stamp has no field and keeps its browser reader (`MatrixChart`), legacy.
 */
export const quickStudyCategoricalGridAdapter: PlotReadAdapter<'stacked-100-bars', StoredQuickStudyCategoricalGrid> = {
  kind: 'stacked-100-bars',
  source: { surface: 'quick-study', shape: 'matrix' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuickStudyCategoricalGrid {
    if (!isRecord(stored)) {
      return false
    }
    const plot = stored.stacked_100_bars_plot
    return isRecord(plot) && plot.kind === 'stacked-100-bars' && isRecord(plot.data) && Array.isArray(plot.data.rows)
  },
  read(stored: StoredQuickStudyCategoricalGrid): StoredPlotPayload<'stacked-100-bars'> {
    return stored.stacked_100_bars_plot as StoredPlotPayload<'stacked-100-bars'>
  },
}
